import { Chip, Typography, Tooltip, useTheme, Theme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl, IntlShape } from 'react-intl';

import { colors } from '@verantos-dev/shared-ui-components/tokens';

import type { MappingResult } from '../types';

/**
 * Extended type for DataGrid rows with rowId
 */
export type MappingResultRow = MappingResult & { rowId: string };

/**
 * Enum for mapping status values (must match backend data values)
 */
export enum MappingStatus {
  MAPPED = 'Mapped',
  UNMAPPED = 'Unmapped',
  NOT_FOUND = 'Not found',
  ALREADY_STANDARD = 'Already standard',
}

/**
 * Status pill colour per the design-review tokens (PR #4021 inline comments):
 * Mapped → `info/main`, Unmapped → the VDS `tertiary-orange` core token (the
 * theme has no tertiary palette slot, so it imports directly per the VDS token
 * rules), Not found → `error/main`, Already standard → `success/main`.
 */
const getStatusPillColor = (status: string, theme: Theme): string => {
  switch (status) {
    case MappingStatus.MAPPED:
      return theme.palette.info.main;
    case MappingStatus.UNMAPPED:
      return colors['tertiary-orange'];
    case MappingStatus.NOT_FOUND:
      return theme.palette.error.main;
    case MappingStatus.ALREADY_STANDARD:
      return theme.palette.success.main;
    default:
      return theme.palette.grey[500];
  }
};

/**
 * Solid-filled status pill with a white label, per the Figma mapping-results
 * design (the earlier soft-tint rendering didn't match the review screens).
 */
const getStatusPillSx = (status: string, theme: Theme) => ({
  backgroundColor: getStatusPillColor(status, theme),
  fontWeight: 600,
  '& .MuiChip-label': { color: theme.palette.common.white },
});

/**
 * Helper function to get localized status text
 */
const getStatusText = (status: string, intl: IntlShape) => {
  switch (status) {
    case MappingStatus.MAPPED:
      return intl.formatMessage({ id: 'codeListMapper.status.mapped' });
    case MappingStatus.UNMAPPED:
      return intl.formatMessage({ id: 'codeListMapper.status.unmapped' });
    case MappingStatus.NOT_FOUND:
      return intl.formatMessage({ id: 'codeListMapper.status.notFound' });
    case MappingStatus.ALREADY_STANDARD:
      return intl.formatMessage({ id: 'codeListMapper.status.alreadyStandard' });
    default:
      return status;
  }
};

/**
 * Custom hook to generate DataGrid columns for mapping results
 *
 * This hook encapsulates the column definitions for the mapping results table,
 * keeping the component file cleaner and making the column configuration reusable.
 *
 * FEATURES:
 * - Consistent typography styling across all columns
 * - Tooltips for all cell values
 * - Status column with color-coded chips and icons
 * - Responsive column widths (fixed and flex)
 * - Memoized for performance
 *
 * USAGE:
 * ```tsx
 * const columns = useMappingResultsColumns();
 * <DataGrid columns={columns} ... />
 * ```
 */
export const useMappingResultsColumns = (): GridColDef<MappingResultRow>[] => {
  const theme = useTheme();
  const intl = useIntl();

  return useMemo(
    () => [
      {
        field: 'type',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.type' }),
        width: 120,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Tooltip title={params.value || ''} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                fontWeight: theme.typography.body2.fontWeight,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'id',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.id' }),
        width: 120,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const notAvailable = intl.formatMessage({ id: 'codeListMapper.common.notAvailable' });
          return (
            <Tooltip title={params.value || notAvailable} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value || notAvailable}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'name',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.name' }),
        flex: 1,
        minWidth: 200,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const notAvailable = intl.formatMessage({ id: 'codeListMapper.common.notAvailable' });
          return (
            <Tooltip title={params.value || notAvailable} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value || notAvailable}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'code',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.code' }),
        width: 160,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const notAvailable = intl.formatMessage({ id: 'codeListMapper.common.notAvailable' });
          return (
            <Tooltip title={params.value || notAvailable} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value || notAvailable}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'vocabulary',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.vocabulary' }),
        width: 130,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const notAvailable = intl.formatMessage({ id: 'codeListMapper.common.notAvailable' });
          return (
            <Tooltip title={params.value || notAvailable} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value || notAvailable}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'standard',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.standard' }),
        width: 110,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          // Determine "Yes" or "No" based on row type and status
          let displayValue = 'No'; // Default to "No"

          if (params.row.type === 'Mapped') {
            // Mapped rows should always be "Yes"
            displayValue = 'Yes';
          } else if (params.row.type === 'Source') {
            // Source rows should be "Yes" only if Status is "Already standard"
            if (
              params.row.status &&
              params.row.status.toString().toLowerCase() === 'already standard'
            ) {
              displayValue = 'Yes';
            } else {
              displayValue = 'No';
            }
          }

          return (
            <Tooltip title={displayValue} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayValue}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'domain',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.domain' }),
        width: 130,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const notAvailable = intl.formatMessage({ id: 'codeListMapper.common.notAvailable' });
          return (
            <Tooltip title={params.value || notAvailable} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value || notAvailable}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'class',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.class' }),
        width: 220,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const notAvailable = intl.formatMessage({ id: 'codeListMapper.common.notAvailable' });
          return (
            <Tooltip title={params.value || notAvailable} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.body2.fontWeight,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value || notAvailable}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'status',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.status' }),
        width: 220,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          // Don't render a status pill for rows without a status value (e.g., nested mapped rows)
          if (!params.value) {
            return null;
          }

          // OR-6288: a soft-filled pill carrying the status and its mapped count
          // (e.g. "Mapped (1)" / "Not found (0)"), matching the Figma. The count
          // comes from the row's mappedCount when it is a finite number.
          const label = getStatusText(params.value, intl);
          const rawCount = params.row.mappedCount;
          const count = typeof rawCount === 'number' ? rawCount : Number(rawCount);
          const pillLabel = Number.isFinite(count) ? `${label} (${count})` : label;

          return (
            <Tooltip title={pillLabel} arrow placement="top">
              <Chip size="small" label={pillLabel} sx={getStatusPillSx(params.value, theme)} />
            </Tooltip>
          );
        },
      },
      {
        field: 'mappedCount',
        headerName: intl.formatMessage({ id: 'codeListMapper.column.mappedCount' }),
        width: 160,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Tooltip title={params.value?.toString() || ''} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                fontWeight: theme.typography.body2.fontWeight,
                color: 'text.primary',
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [theme, intl]
  );
};
